import type { PageComponentProps } from '@/features/a-dynamic-page/components/page-component-switch';
import type {
    FilterDataV1_jsonld_page_read,
    FilterDataV1Attributes,
    FilterDataV1Meta,
    FilterGroupV1,
} from '@/features/filter/filter-data-v1-type';

import React from 'react';

import useComponentDataByIri from '@/core/features/a-component/hooks/use-component-data-by-iri';
import { updateComponentIriParam } from '@/core/features/a-component/services/component-service';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { useHydrateFilterDataV1Attributes } from '@/features/filter/filter-data/filter-data-attributes-state';
import { useFilterFetching, useFilterOverlayToggle } from '@/features/filter/filter-data/filter-state';
import { useQuickFilterOverlayToggle } from '@/features/filter/quick-filter/quick-filter/quick-filter-state';

export default function FilterDataV1(props: PageComponentProps<FilterDataV1Attributes, FilterDataV1Meta>) {
    const setFilterDataV1Attributes = useHydrateFilterDataV1Attributes(props.attributes);
    const [_, setFilterFetching] = useFilterFetching();

    const { isOverlayOpen } = useFilterOverlayToggle({ overlayKey: 'filter-data-overlay' });
    const { isDesktopView } = useDeviceoutput();
    const { isQuickFilterOverlayOpen } = useQuickFilterOverlayToggle();
    const loadFilter = isDesktopView || isOverlayOpen || isQuickFilterOverlayOpen;

    const boundariesRequestTarget: null | string = React.useMemo(() => {
        if (!loadFilter) {
            return null;
        }
        if (!props.meta?.boundariesRequestTarget) {
            return null;
        }

        return updateComponentIriParam('userQuery', props.meta.boundariesRequestTarget).toString();
    }, [props.meta, loadFilter]);

    const { data: mapBoundariesData, isLoading: isMapBoundariesLoading } =
        useComponentDataByIri<FilterDataV1_jsonld_page_read>(boundariesRequestTarget, {
            queryName: 'filterDataBoundariesRequestTarget',
        });

    const { data: countData, isLoading: isCountLoading } = useComponentDataByIri<FilterDataV1_jsonld_page_read>(
        loadFilter ? (props.meta?.countRequestTarget ?? null) : null,
        { queryName: 'filterDataCountRequestTarget' },
    );

    const { data: countDataByBoundaries, isLoading: isCountLoadingByBoundaries } =
        useComponentDataByIri<FilterDataV1_jsonld_page_read>(
            loadFilter ? (mapBoundariesData?.meta.countRequestTarget ?? null) : null,
            { queryName: 'filterDataCountRequestTargetByBoundaries' },
        );

    const filterAttributes = mapBoundariesData?.attributes ?? props.attributes;

    const countDataAttributes = countDataByBoundaries?.attributes ?? countData?.attributes;

    const mergedFilterAttributes: FilterDataV1Attributes | null = React.useMemo(() => {
        const mergeFilterGroupCounts = (
            filterGroups: FilterGroupV1[] = filterAttributes.groups,
            filterGroupCounts: FilterGroupV1[] = countDataAttributes?.groups ?? [],
        ): FilterGroupV1[] => {
            return filterGroups.map((group) => {
                const groupCounts = filterGroupCounts.find((countGroup) => countGroup.name === group.name);
                if (!groupCounts) {
                    return group;
                }

                if (group.groups && group.groups.length > 0) {
                    return {
                        ...group,
                        groups: mergeFilterGroupCounts(group.groups, groupCounts.groups ?? []),
                    };
                }

                return {
                    ...group,
                    options: group.options.map((option) => {
                        const dataOption = groupCounts.options.find((countOption) => option.name === countOption.name);
                        if (!dataOption) {
                            return option;
                        }

                        return {
                            ...option,
                            count: dataOption.count,
                        };
                    }),
                };
            });
        };

        return {
            ...filterAttributes,
            groups: mergeFilterGroupCounts(filterAttributes.groups, countDataAttributes?.groups ?? []),
        };
    }, [countDataAttributes?.groups, filterAttributes]);

    React.useEffect(() => {
        if (!mergedFilterAttributes) {
            return;
        }

        setFilterDataV1Attributes(mergedFilterAttributes);
    }, [mergedFilterAttributes, setFilterDataV1Attributes]);

    React.useEffect(() => {
        setFilterFetching(isCountLoadingByBoundaries || isCountLoading || isMapBoundariesLoading);
    }, [isCountLoading, isCountLoadingByBoundaries, isMapBoundariesLoading, setFilterFetching]);

    return null;
}
